import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { navigateToTeamMemberFromBreadcrumb, useAthleteStaffForAdminBreadcrumb } from "./AthleteAdminBreadcrumbNav";
import VideoLightbox from "./VideoLightbox";
import { standaloneVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import Pagination from "./Pagination";
import ExpertModal from "./ExpertModal";
import { isAthlete } from "../utils/auth";
import { buildApiUrl } from "../utils/apiConfig";
import { decodeHtmlEntities } from "../utils/htmlUtils";
import { ExpertDto } from "../types/resources";
import "../styles/spotlight.css";


interface SpotlightResource {
    id: number;
    slug: string;
    title: string;
    course_expert?: number;
    excerpt?: string;
    img?: string;
    subtitle?: string;
    image?: string;
    video_url?: string;
    is_featured: boolean;
    expert?: ExpertDto;
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

export default function SpotlightPage() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [featuredResource, setFeaturedResource] = useState<SpotlightResource | null>(null);
    const [pastResources, setPastResources] = useState<SpotlightResource[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 3;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<{ id: number; title: string; vimeoUrl: string } | null>(null);
    const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({});
    const [imageErrorStates, setImageErrorStates] = useState<Record<number, boolean>>({});
    const [selectedExpert, setSelectedExpert] = useState<ExpertDto | null>(null);
    const [isExpertModalOpen, setIsExpertModalOpen] = useState(false);

    const isAdminViewingAthlete = !isAthlete() && athleteId;

    const navState = location.state as { teamMemberId?: string; teamMemberName?: string } | null;
    const teamMemberId = navState?.teamMemberId;
    const teamMemberName = navState?.teamMemberName;

    const { staffId: athleteStaffId } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId)
    );

    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                if (teamMemberName) {
                    const nameParts = teamMemberName.split(' ');
                    setCoachData({
                        id: teamMemberId,
                        firstName: nameParts[0] || teamMemberName,
                        lastName: nameParts.slice(1).join(' ') || '',
                    });
                }

                const response = await fetch(
                    buildApiUrl(`/api/spotlight/resources`),
                    {
                        headers: { Accept: "application/json" },
                        credentials: "include"
                    }
                );

                if (!response.ok) {
                    throw new Error(`Failed to fetch Spotlight content: ${response.status}`);
                }

                const data = await response.json();
                const resources = data.resources || [];

                const featured = resources.find((item: SpotlightResource) => item.is_featured);
                const past = resources.filter((item: SpotlightResource) => !item.is_featured);

                setFeaturedResource(featured || null);
                setPastResources(past);

                const initialLoadingStates: Record<number, boolean> = {};
                resources.forEach((item: SpotlightResource) => {
                    initialLoadingStates[item.id] = false;
                });
                setImageLoadingStates(initialLoadingStates);
            } catch (error) {
                setError("Failed to load Spotlight content");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [athleteId, teamMemberId, teamMemberName]);

    const handleVideoClick = (item: SpotlightResource) => {
        if (!item.video_url) return;

        setSelectedVideo({
            id: item.id,
            title: item.title,
            vimeoUrl: item.video_url
        });
    };

    const handleImageLoad = (itemId: number) => {
        setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
    };

    const handleImageError = (itemId: number) => {
        setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
        setImageErrorStates(prev => ({ ...prev, [itemId]: true }));
    };

    const handleLaunchClick = (item: SpotlightResource) => {
        if (item.video_url) {
            handleVideoClick(item);
        }
    };

    const handleExpertClick = (expert: ExpertDto) => {
        setSelectedExpert(expert);
        setIsExpertModalOpen(true);
    };

    if (loading) {
        return (
            <div className={`spotlight-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                <div className="spotlight-container">
                    <div className="loading-spinner">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`spotlight-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                <div className="spotlight-container">
                    <div className="error-message">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`spotlight-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
            {isAdminViewingAthlete && (
                <div className="spotlight-breadcrumb-nav">
                    <button
                        onClick={handleNavigateToTeamMember}
                        className="spotlight-breadcrumb-link"
                        data-testid="breadcrumb-back-button"
                    >
                        <svg
                            className="spotlight-breadcrumb-icon"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        <span>
                            Back to {coachData?.firstName} {coachData?.lastName}'s Profile
                        </span>
                    </button>
                </div>
            )}

            <div className={`spotlight-container ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>

                {featuredResource && (
                    <section className="spotlight-featured-section">
                        <div className="spotlight-featured-card">
                            <div className="spotlight-featured-content">
                                <div className="spotlight-featured-logo">
                                    {imageLoadingStates[featuredResource.id + 1000] && !imageErrorStates[featuredResource.id + 1000] && (
                                        <div className="spotlight-logo-loading">
                                            <div className="spotlight-logo-spinner"></div>
                                        </div>
                                    )}
                                    <img
                                        src={imageErrorStates[featuredResource.id + 1000] ? "/api/placeholder/200/200" : (featuredResource.image || featuredResource.img || "")}
                                        alt={`${featuredResource.title} logo`}
                                        className="spotlight-logo-image"
                                        onLoad={() => handleImageLoad(featuredResource.id + 1000)}
                                        onError={() => handleImageError(featuredResource.id + 1000)}
                                    />
                                </div>
                                <div className="spotlight-featured-expert">
                                    {featuredResource.expert?.expertImageUrl && (
                                        <img
                                            src={featuredResource.expert.expertImageUrl}
                                            alt={featuredResource.expert.title || "Expert"}
                                            className="spotlight-expert-img"
                                        />
                                    )}
                                    {featuredResource.expert?.title && (
                                        <button
                                            className="spotlight-expert-name spotlight-expert-link"
                                            onClick={() => featuredResource.expert && handleExpertClick(featuredResource.expert)}
                                        >
                                            {featuredResource.expert.title}
                                        </button>
                                    )}
                                </div>
                                <h2 className="spotlight-featured-title">
                                    {decodeHtmlEntities(featuredResource.title)}
                                </h2>
                                {featuredResource.excerpt && (
                                    <div
                                        className="spotlight-featured-excerpt"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(decodeHtmlEntities(featuredResource.excerpt)) }}
                                    />
                                )}
                                <button
                                    className="spotlight-launch-btn"
                                    onClick={() => handleLaunchClick(featuredResource)}
                                >
                                    Launch
                                </button>
                            </div>

                            <div
                                className="spotlight-featured-video"
                                data-testid="featured-resource-video"
                            >
                                {imageLoadingStates[featuredResource.id] && !imageErrorStates[featuredResource.id] && (
                                    <div className="spotlight-image-loading">
                                        <div className="spotlight-image-spinner"></div>
                                    </div>
                                )}
                                <img
                                    src={imageErrorStates[featuredResource.id] ? "/api/placeholder/800/450" : (featuredResource.img || featuredResource.image || "")}
                                    alt={featuredResource.title}
                                    className="spotlight-featured-image"
                                    onLoad={() => handleImageLoad(featuredResource.id)}
                                    onError={() => handleImageError(featuredResource.id)}
                                />
                            </div>
                        </div>
                    </section>
                )}

                {pastResources.length > 0 && (
                    <section className="spotlight-past-section">
                        <h2 className="spotlight-section-title">Past Spotlights</h2>
                        <div className="spotlight-grid">
                            {pastResources
                                .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
                                .map((item) => (
                                <div
                                    key={item.id}
                                    className="spotlight-card"
                                    data-testid={`past-resource-${item.slug}`}
                                >
                                    <div
                                        className="spotlight-card-video"
                                        data-testid={`past-resource-video-${item.id}`}
                                    >
                                        {imageLoadingStates[item.id] && !imageErrorStates[item.id] && (
                                            <div className="spotlight-image-loading">
                                                <div className="spotlight-image-spinner"></div>
                                            </div>
                                        )}
                                        <img
                                            src={imageErrorStates[item.id] ? "/api/placeholder/400/225" : (item.img || item.image || "")}
                                            alt={item.title}
                                            className="spotlight-card-image"
                                            onLoad={() => handleImageLoad(item.id)}
                                            onError={() => handleImageError(item.id)}
                                        />
                                    </div>

                                    <div className="spotlight-card-content">
                                        <div className="spotlight-card-logo">
                                            {imageLoadingStates[item.id + 1000] && !imageErrorStates[item.id + 1000] && (
                                                <div className="spotlight-logo-loading-small">
                                                    <div className="spotlight-logo-spinner-small"></div>
                                                </div>
                                            )}
                                            <img
                                                src={imageErrorStates[item.id + 1000] ? "/api/placeholder/60/60" : (item.image || item.img || "")}
                                                alt={`${item.title} logo`}
                                                className="spotlight-card-logo-image"
                                                onLoad={() => handleImageLoad(item.id + 1000)}
                                                onError={() => handleImageError(item.id + 1000)}
                                            />
                                        </div>
                                        <div className="spotlight-card-expert">
                                            {item.expert?.expertImageUrl && (
                                                <img
                                                    src={item.expert.expertImageUrl}
                                                    alt={item.expert.title || "Expert"}
                                                    className="spotlight-card-expert-img"
                                                />
                                            )}
                                            {item.expert?.title && (
                                                <button
                                                    className="spotlight-card-expert-name spotlight-expert-link"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        item.expert && handleExpertClick(item.expert);
                                                    }}
                                                >
                                                    {item.expert.title}
                                                </button>
                                            )}
                                        </div>
                                        <h3 className="spotlight-card-title">
                                            {decodeHtmlEntities(item.title)}
                                        </h3>
                                        {item.excerpt && (
                                            <div
                                                className="spotlight-card-excerpt"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(decodeHtmlEntities(item.excerpt)) }}
                                            />
                                        )}
                                        <button
                                            className="spotlight-launch-btn spotlight-launch-btn-small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLaunchClick(item);
                                            }}
                                        >
                                            Launch
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {pastResources.length > itemsPerPage && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(pastResources.length / itemsPerPage)}
                                totalElements={pastResources.length}
                                onPageChange={setCurrentPage}
                                pageSize={itemsPerPage}
                            />
                        )}
                    </section>
                )}

                {!featuredResource && pastResources.length === 0 && (
                    <div className="spotlight-empty">
                        <svg
                            className="spotlight-empty-icon"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                        </svg>
                        <p className="spotlight-empty-text">No content available at this time.</p>
                    </div>
                )}
            </div>

            <VideoLightbox
                open={!!selectedVideo}
                resource={selectedVideo ?? undefined}
                onClose={() => setSelectedVideo(null)}
                analytics={selectedVideo ? standaloneVideoAnalytics(WORDPRESS_TAB.SPOTLIGHT, selectedVideo.id) : null}
            />

            <ExpertModal
                isOpen={isExpertModalOpen}
                expert={selectedExpert}
                onClose={() => {
                    setIsExpertModalOpen(false);
                    setSelectedExpert(null);
                }}
            />
        </div>
    );
}

